import type * as React from "react";
import { type FC, memo, type ReactNode } from "react";
import { DateFormatType, formatDate } from "~/utils/DateFormatter";
import { PackageEntrySource } from "~/feature/cart/cart/models/CartData";
import Spacer from "~/common/components/Spacer";
import * as PriceUtils from "~/utils/PriceUtils";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { HAS_END_CUSTOMER } from "~/utils/constants";
import { Permission, userHasPermission } from "~/utils/permissions";
import SimplePriceText, { PriceTextVariants } from "~/common/components/SimplePriceText/SimplePriceText";
import OptimeSolutionsMessage from "~/common/components/Messages/OptimeSolutionsMessage";
import { ViewablePackagingCell } from "~/feature/cart/cart/components/CartPage/ProductTable/EditableEntry/PackagingCell";
import { AliasesParameter, GPSDParameter, IncludedSurcharge } from "~/common/components/Product/ProductParams";
import {
  ContractItemParam,
  QuoteItemParam,
  userCanSeePriceReference,
} from "~/feature/cart/cart/components/CartPage/LineItemStaticParams";
import { NumericFormat } from "react-number-format";
import { NumberFormatter as NF } from "~/utils/NumberFormatter";
import { selectPackageEntry, selectProductCodeFromPackage } from "~/feature/cart/cart/redux/selectors/cartSelectors";
import { getEDDLabel } from "~/feature/cart/cart/utils/cartUtils";
import {
  getSurchargeValueFromTotalPrice,
  getTotalPriceForLineItem,
  isProductPMA,
} from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { isKITKITProduct } from "~/models/ProductData";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import Parameter from "~/common/components/Parameter";
import {
  isPartialDeliveriesVisible,
  PartialDeliveriesSection,
} from "~/feature/cart/cart/components/CartPage/ProductTable/EditableEntry/PartialDeliveriesSection";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";

import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { LineItemStockIndicator } from "~/feature/cart/cart/components/CartPage/ProductTable/EditableEntry/LineItemStockIndicator";
import { ProductService } from "~/AddToCart/ProductService";
import { Alert } from "~/common/m3/Alert/Alert";
import { PriceElements } from "~/common/components/Price/PriceV4";
import { Stack } from "@mui/material";

interface Props {
  packEntryId: number;
  index: number;
}

const PackageEntry = ({ packEntryId, index }: Props) => {
  const { t } = useTranslation();
  const packEntry = useSelector(selectPackageEntry(packEntryId));
  const productCode = useSelector(selectProductCodeFromPackage(packEntryId));

  const product = ProductService.useProductDescriptor({
    productCode,
    priceReference: packEntry.priceReference,
    // options: { fetch: true },
  });

  const isBulkUploadEntry = packEntry.source === PackageEntrySource.BULK;
  const quantityVisible = product.base.quantityVisible;
  const isOptime = product.base.isOptime;

  let price;
  let totalPrice;
  let hasPrice = false;
  let surchargeValue;
  let hasSurcharge;

  if (isBulkUploadEntry || !packEntry.netPrice) {
    price = "...";
    totalPrice = "...";
    surchargeValue = undefined;
    hasSurcharge = false;
  } else {
    hasSurcharge = (packEntry.netPrice?.surcharge ?? 0) > 0;

    const totalPriceVal = getTotalPriceForLineItem(packEntry, product);
    surchargeValue = hasSurcharge ? getSurchargeValueFromTotalPrice(totalPriceVal) : undefined;

    if (packEntry.netPrice && packEntry.netPrice.value > 0) {
      hasPrice = true;
    } else {
      hasSurcharge = false;
    }
    totalPrice = PriceUtils.text(totalPriceVal, "-");
  }

  const tariffSurcharge = packEntry.netPrice?.tariffSurcharge;

  const zeroPriceLabel = isProductPMA(product) || isKITKITProduct(product.base) ? "-" : "product.priceOnRequest";

  const rdd = packEntry.deliveryDate ? formatDate(packEntry.deliveryDate, DateFormatType.DATE) : "–";
  // TODO: Fix padding bottom on cell with price reference - all inputs need to always take space for the helper label, even if it's not present....
  const showPriceReference = userCanSeePriceReference();

  const userHasEndCustomerPermission = userHasPermission(Permission.SEE_ENDCUSTOMER_TABULAR_VIEW);
  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const endCustomerLabel: () => string = () => {
    let endCustomerLabel = "-";
    window.SCHA.config.priceReferences.map((option) => {
      if (option.value === packEntry.priceReference) {
        endCustomerLabel = option.label;
      }
    });
    return endCustomerLabel;
  };

  const isSpecial = isSpecialPrice(product, packEntry.packCode);

  const showEdd = true;

  const saleActive = packEntry.saleCampaignEnabled;

  let priceEl;
  if (webservicesDisabled) {
    priceEl = null;
    // SCHAEFF-31658
    //
    // } else if (saleActive) {
    //   if (product.isPriceLoading) {
    //     priceEl = <CircleSpinner />;
    //   } else {
    //     const netPrice = packEntry.netPrice;
    //     const salePrice = packEntry.actualSalePrice;
    //     const lowerPrice = lower(packEntry.actualNetPrice, salePrice);
    //
    //     priceEl = (
    //       <div className={classes.pricing}>
    //         {lowerPrice?.value !== packEntry.actualNetPrice?.value && packEntry.actualNetPrice?.value !== 0 && (
    //           <PriceV3 price={packEntry.actualNetPrice} className={classes.regularPrice} />
    //         )}
    //         <PriceV3 data-test-id={"package-entry-net-price"} price={lowerPrice} className={classes.salePrice} />
    //         <PriceV3.NetPriceUnit price={netPrice} className={classes.priceUnit} variant={"short"} />
    //       </div>
    //     );
    //   }
  } else {
    priceEl = (
      <PriceElements.Stack gap={0}>
        <PriceElements.Price price={packEntry.netPrice} zeroText={zeroPriceLabel} tariffSurcharge={tariffSurcharge} />
        <PriceElements.NetPriceLabel price={packEntry.netPrice} labelVariant="short" color="textSecondary" />
      </PriceElements.Stack>
    );
  }

  return (
    <div data-test-id={`package-entry`}>
      <CellRow>
        <Cell first={true} data-test-id={"package-entry-quantity"}>
          <NumericFormat
            value={packEntry.quantity}
            displayType={"text"}
            thousandSeparator={NF.getThousandSeparator()}
            decimalSeparator={NF.getDecimalSeparator()}
          />
        </Cell>
        <Cell>
          <div>
            <ViewablePackagingCell lineItem={packEntry} product={product} />

            <AliasesParameter
              className={"mt-3"}
              product={product.base}
              mode={"15-digits"}
              packCode={packEntry.packCode}
            />

            {isLineLevelCustomerOrderNumberEnabled() && packEntry.customerOrderNumber && (
              <Parameter className={"mt-3"} label={t("search.type.customerOrderNumber")}>
                {packEntry.customerOrderNumber}
              </Parameter>
            )}
          </div>
        </Cell>

        <Cell>
          {quantityVisible && !isProductPMA(product) ? (
            <div>
              <p className={"my-0"}>{rdd}</p>
              <p className={"mb-0 mt-1 f-label-color"}>{showEdd ? getEDDLabel(packEntry, t) : "-"}</p>
            </div>
          ) : (
            <div>{"-"}</div>
          )}
        </Cell>

        <Cell>
          <LineItemStockIndicator product={product} packEntry={packEntry} />
        </Cell>

        {userHasEndCustomerPermission
          ? HAS_END_CUSTOMER && <Cell className={"pb-3 d-flex flex-direction-column"}>{endCustomerLabel()}</Cell>
          : showPriceReference && (
              <Cell className={"pb-3 d-flex flex-direction-column"} data-test-id={"package-entry-price-reference"}>
                {packEntry.priceReference}
              </Cell>
            )}

        <Cell className={"d-flex flex-direction-column"}>
          {priceEl}
          <QuoteItemParam product={product.base} lineItem={packEntry} />
          <ContractItemParam product={product.base} lineItem={packEntry} />
        </Cell>

        <Cell className={"d-flex flex-direction-column"} last={true}>
          {!webservicesDisabled && (
            <strong data-test-id={"package-entry-total-price"}>
              {totalPrice}
              {hasSurcharge && "*"}
            </strong>
          )}
          {!quantityVisible && (
            <div className={"mt-1 f-label-color"}>
              <SimplePriceText
                priceUnit={packEntry.netPrice?.unit ?? 1}
                variant={quantityVisible ? null : PriceTextVariants.yearlySubscription}
              />
            </div>
          )}

          <IncludedSurcharge className={"mt-3"} value={surchargeValue} percent={packEntry.netPrice?.surcharge ?? 0} />

          <GPSDParameter product={product.base} />
        </Cell>
      </CellRow>
      {packEntry.comment && (
        <Row className={"mt-3"}>
          <p className={"my-0 f-label-color"}>{t("wishlistPage.comment.label")}</p>
          <p className={"mt-1 mb-0"} data-test-id={"package-entry-comment"}>
            {packEntry.comment}
          </p>
        </Row>
      )}
      {/* spacer without border */}
      {(!quantityVisible || isOptime) && <div className={"mt-2 mb-0"} />}
      <Stack gap={1} padding={2}>
        {!quantityVisible && <Alert severity="info">{t("subscriptionProduct.DST.billingInfo")}</Alert>}
        {isProductPMA(product) && <InfoProductMessagePMA />}
        {isOptime && <OptimeSolutionsMessage />}
      </Stack>

      {isPartialDeliveriesVisible(packEntry.partialDeliveries) && (
        <div className={"scha__cart__product-table__right-side__partial-deliveries-container"}>
          <PartialDeliveriesSection
            partialDeliveries={packEntry.partialDeliveries}
            packagingIsExpanded={packEntry.isPackagingSelectionOpen}
            isCheckout={true}
          />
        </div>
      )}

      <Spacer className={"mt-3 mb-0"} />
    </div>
  );
};

const CellRow = ({ children }: { children?: ReactNode }) => (
  <div className={"scha__cart__product-table__right-side__col-container"}>{children}</div>
);

type CellProps = { alignEnd?: boolean; first?: boolean; last?: boolean };
const Cell: FC<CellProps & JSX.IntrinsicElements["div"]> = ({ alignEnd, first, last, className, ...rest }) => {
  const cls = classNames(
    "scha__cart__product-table__body-cell",
    {
      "first-on-right": first,
      "last-on-right": last,
      "flex-justify-end d-flex": alignEnd,
    },
    className,
  );
  return <div className={cls} {...rest} />;
};

const Row: React.FunctionComponent<{ className?: string; children?: ReactNode }> = ({ children, className }) => (
  <div className={classNames(className, "px-3")}>{children}</div>
);

export default memo(PackageEntry);
